/**
 * Admin V3 — Pricing — Schedule A client-side validation
 *
 * Pure function: validates a proposed schedule_a payload against the
 * canonical Gate A + Gate B + Gate A.1 + G2 + G5 constraints.
 *
 * Defense-in-depth: the backend Pydantic ScheduleAPayload validator is
 * authoritative. These client-side checks render inline errors and prevent
 * obvious mistakes before round-tripping to the server.
 *
 * Hard rules enforced:
 *   - Exactly 3 bands; bands[0] flat, bands[1] flat, bands[2] additive.
 *   - bands[0].upper_km < bands[1].upper_km; bands[2].upper_km === null.
 *   - class_multipliers MUST NOT contain a 'priority' key (case-insensitive).
 *   - fixed_routes[*].bidirectional must be boolean (Gate B unidirectional default).
 *   - fixed_routes[*].service_types must be a subset of ['standard','corporate','partner'].
 *
 * Returns: { ok: boolean, errors: { [path]: string } }
 *   - errors keys are dotted paths (e.g. 'bands.0.flat_fare', 'class_multipliers').
 */

const SUPPORTED_BAND_MODES = ['flat', 'additive'];
const SUPPORTED_SERVICE_TYPES = ['standard', 'corporate', 'partner'];

/**
 * Case-insensitive check for the presence of a 'priority' key in
 * class_multipliers. Mirrors the backend Pydantic ScheduleAPayload
 * validator behavior.
 */
export const hasPriorityKey = (classMultipliers) => {
  if (!classMultipliers || typeof classMultipliers !== 'object') return false;
  return Object.keys(classMultipliers).some(
    (k) => typeof k === 'string' && k.toLowerCase() === 'priority',
  );
};

const isPositiveNumber = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0;
const isNonNegativeNumber = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0;

const validateBands = (bands, errors) => {
  if (!Array.isArray(bands)) {
    errors['bands'] = 'bands must be an array';
    return;
  }
  if (bands.length !== 3) {
    errors['bands'] = `expected exactly 3 bands, got ${bands.length}`;
    return;
  }

  // Per-band shape
  bands.forEach((band, i) => {
    if (!band || typeof band !== 'object') {
      errors[`bands.${i}`] = 'band must be an object';
      return;
    }
    if (!SUPPORTED_BAND_MODES.includes(band.mode)) {
      errors[`bands.${i}.mode`] = `mode must be 'flat' or 'additive', got ${JSON.stringify(band.mode)}`;
    }
  });

  // Band 0 + 1 must be flat with positive flat_fare and null additive fields.
  [0, 1].forEach((i) => {
    const b = bands[i];
    if (!b) return;
    if (b.mode === 'flat') {
      if (!isPositiveNumber(b.flat_fare)) {
        errors[`bands.${i}.flat_fare`] = 'flat_fare must be a positive number';
      }
    }
  });

  // Band 2 must be additive with null upper_km and positive additive params.
  const b2 = bands[2];
  if (b2 && b2.mode === 'additive') {
    if (b2.upper_km !== null && b2.upper_km !== undefined) {
      errors['bands.2.upper_km'] = 'band 2 upper_km must be null (open tail)';
    }
    if (!isPositiveNumber(b2.additive_base)) {
      errors['bands.2.additive_base'] = 'additive_base must be a positive number';
    }
    if (!isPositiveNumber(b2.additive_rate_per_km)) {
      errors['bands.2.additive_rate_per_km'] = 'additive_rate_per_km must be a positive number';
    }
    if (!isNonNegativeNumber(b2.additive_lower_km)) {
      errors['bands.2.additive_lower_km'] = 'additive_lower_km must be a non-negative number';
    }
  }

  // Upper-km ordering (bands 0, 1)
  if (
    bands[0] && bands[1]
    && typeof bands[0].upper_km === 'number'
    && typeof bands[1].upper_km === 'number'
    && bands[0].upper_km >= bands[1].upper_km
  ) {
    errors['bands.upper_km_order'] = 'bands[0].upper_km must be < bands[1].upper_km';
  }
};

const validateClassMultipliers = (cm, errors) => {
  if (!cm || typeof cm !== 'object') {
    errors['class_multipliers'] = 'class_multipliers must be an object';
    return;
  }
  // Gate A.1 — Priority hard rejection (case-insensitive)
  if (hasPriorityKey(cm)) {
    errors['class_multipliers.priority'] = "Gate A.1 violation: 'priority' key is forbidden in class_multipliers";
  }
  // Required + supported keys must be positive numbers
  ['standard', 'corporate', 'partner'].forEach((k) => {
    if (cm[k] !== undefined && !isPositiveNumber(cm[k])) {
      errors[`class_multipliers.${k}`] = `${k} multiplier must be a positive number`;
    }
  });
  // business / van are persisted-but-dormant; if present they must be positive
  ['business', 'van'].forEach((k) => {
    if (cm[k] !== undefined && !isPositiveNumber(cm[k])) {
      errors[`class_multipliers.${k}`] = `${k} multiplier must be a positive number`;
    }
  });
};

const validateFixedRoutes = (routes, errors) => {
  if (!Array.isArray(routes)) {
    errors['fixed_routes'] = 'fixed_routes must be an array';
    return;
  }
  routes.forEach((r, i) => {
    if (!r || typeof r !== 'object') {
      errors[`fixed_routes.${i}`] = 'route must be an object';
      return;
    }
    if (typeof r.route_id !== 'string' || !r.route_id) {
      errors[`fixed_routes.${i}.route_id`] = 'route_id must be a non-empty string';
    }
    if (typeof r.name !== 'string' || !r.name) {
      errors[`fixed_routes.${i}.name`] = 'name must be a non-empty string';
    }
    if (!Array.isArray(r.pickup_keywords) || r.pickup_keywords.length === 0) {
      errors[`fixed_routes.${i}.pickup_keywords`] = 'pickup_keywords must be a non-empty array';
    }
    if (!Array.isArray(r.dropoff_keywords) || r.dropoff_keywords.length === 0) {
      errors[`fixed_routes.${i}.dropoff_keywords`] = 'dropoff_keywords must be a non-empty array';
    }
    if (!isPositiveNumber(r.fixed_price)) {
      errors[`fixed_routes.${i}.fixed_price`] = 'fixed_price must be a positive number';
    }
    if (typeof r.bidirectional !== 'boolean') {
      errors[`fixed_routes.${i}.bidirectional`] = 'bidirectional must be a boolean (Gate B default: false)';
    }
    if (!Array.isArray(r.service_types) || r.service_types.length === 0) {
      errors[`fixed_routes.${i}.service_types`] = 'service_types must be a non-empty array';
    } else {
      const invalid = r.service_types.filter((st) => !SUPPORTED_SERVICE_TYPES.includes(st));
      if (invalid.length > 0) {
        errors[`fixed_routes.${i}.service_types`] = `service_types must be a subset of ${JSON.stringify(SUPPORTED_SERVICE_TYPES)}; got invalid ${JSON.stringify(invalid)}`;
      }
    }
  });
};

/**
 * Main validation entrypoint.
 *
 * @param {Object} scheduleA - the proposed schedule_a object
 * @returns {{ ok: boolean, errors: Object<string,string> }}
 */
export const validateScheduleA = (scheduleA) => {
  const errors = {};
  if (!scheduleA || typeof scheduleA !== 'object') {
    return { ok: false, errors: { schedule_a: 'schedule_a must be an object' } };
  }
  validateBands(scheduleA.bands, errors);
  validateClassMultipliers(scheduleA.class_multipliers, errors);
  validateFixedRoutes(scheduleA.fixed_routes, errors);
  return { ok: Object.keys(errors).length === 0, errors };
};

export default validateScheduleA;
